<template>
  <LoadingSpinner v-if="!isReadyPrint" :message="'Đang tải dữ liệu in ...'" />
  <div v-else>
    <div style="margin: 0 20px; font-family: 'Times New Roman', Times, serif">
      <!-- Header -->
      <b-row class="mb-2">
        <b-col class="d-flex align-items-center">
          <img
            :src="SUPPLIER_INFO.logo"
            alt="Logo"
            style="height: 70px; width: auto; margin-right: 10px; object-fit: contain"
          />
          <div>
            <p class="label-color font-size-print m-0 font-weight-bold">{{ SUPPLIER_INFO.companyName }}</p>
            <p class="font-size-print m-0">
              Địa chỉ: {{ SUPPLIER_INFO.address }} &nbsp;|&nbsp; MST: {{ SUPPLIER_INFO.taxCode }}
            </p>
          </div>
        </b-col>
      </b-row>

      <div class="text-center mb-4">
        <p class="label-color print-bb-title m-0">PHỤ LỤC DANH SÁCH IMEI / SERIAL NUMBER</p>
      </div>

      <b-row class="mb-2">
        <b-col cols="6">
          <p class="font-size-print m-0">
            <strong>Số HĐ:</strong> {{ bill.einvoice && bill.einvoice.invoiceNo ? bill.einvoice.invoiceNo : '' }}
          </p>
        </b-col>
        <b-col cols="6" class="text-right">
          <p class="font-size-print m-0">
            <strong>Ngày:</strong> {{ formattedCreatedDate }}
          </p>
        </b-col>
      </b-row>

      <b-row class="mb-2">
        <b-col cols="8">
          <p class="font-size-print m-0">
            <strong>Khách hàng:</strong> {{ bill.customerCompanyName || bill.customerName }}
          </p>
        </b-col>
        <b-col cols="4" class="text-right">
          <p class="font-size-print m-0">
            <strong>Tổng SL hàng hóa:</strong> {{ bill.sumQuantity }}
          </p>
        </b-col>
      </b-row>

      <table class="table table-bordered-print col-md-12 mb-4" style="table-layout: fixed; width: 100%">
        <colgroup>
          <col style="width: 5%" />
          <col style="width: 82%" />
          <col style="width: 7%" />
          <col style="width: 6%" />
        </colgroup>
        <tr>
          <th class="label-color text-center font-size-print">STT</th>
          <th class="label-color font-size-print">Tên hàng hóa</th>
          <th class="label-color text-center font-size-print">ĐVT</th>
          <th class="label-color text-center font-size-print">SL</th>
        </tr>
        <template v-for="(item, index) in groupedItems">
          <tr v-if="item.billItemType === BILL_ITEM_TYPE_PRODUCT" :key="'item-' + index">
            <td class="text-center align-middle font-size-print">{{ item.stt }}</td>
            <td class="align-middle font-size-print">{{ item.name }}</td>
            <td class="text-center align-middle font-size-print print-uom" :rowspan="item.imeiCodes ? 2 : 1">
              {{ item.productUnit || 'Cái' }}
            </td>
            <td class="text-center align-middle font-size-print print-qty" :rowspan="item.imeiCodes ? 2 : 1">
              {{ item.quantity }}
            </td>
          </tr>
          <tr v-if="item.billItemType === BILL_ITEM_TYPE_PRODUCT && item.imeiCodes" :key="'imei-' + index">
            <td colspan="2" class="font-size-print imei-cell" style="padding: 4px 8px; border-top: none">
              <p class="m-0">{{ item.imeiCodes }}</p>
            </td>
          </tr>
        </template>
        <tr>
          <td colspan="3" class="text-right align-middle font-size-print font-weight-bold">
            Tổng cộng
          </td>
          <td class="text-center align-middle font-size-print font-weight-bold">
            {{ bill.sumQuantity }}
          </td>
        </tr>
      </table>

      <!-- Ký tên -->
      <b-row class="mt-4">
        <b-col class="text-center">
          <p class="font-size-print font-weight-bold m-0">ĐẠI DIỆN BÊN NHẬN</p>
          <p class="font-size-print m-0">(Ký, ghi rõ họ tên)</p>
          <br /><br /><br />
        </b-col>
        <b-col class="text-center">
          <p class="font-size-print font-weight-bold m-0">ĐẠI DIỆN BÊN GIAO</p>
          <p class="font-size-print m-0">(Ký, ghi rõ họ tên)</p>
          <br /><br /><br />
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import { cloneDeep } from '@/utils/common';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';
import { BILL_ITEM_TYPE } from '@/utils/enum';
import { SUPPLIER_INFO } from '@/utils/constants';
import { groupBy } from 'lodash';

export default {
  name: 'PrintBillPhuLucImei',
  components: { LoadingSpinner },
  props: ['printData'],
  data() {
    return {
      SUPPLIER_INFO,
      BILL_ITEM_TYPE_PRODUCT: BILL_ITEM_TYPE.PRODUCT,
      bill: {},
      groupedItems: [],
      isReadyPrint: false,
    };
  },
  watch: {
    printData: {
      async handler(newPrintData) {
        if (!newPrintData?.bill) return;
        const billCopy = cloneDeep(newPrintData.bill);
        this.groupedItems = this.groupBillItems(billCopy.listBillItem || []);
        this.bill = billCopy;
        await this.$nextTick();
        setTimeout(() => {
          this.isReadyPrint = true;
        }, 500);
      },
      deep: false,
      immediate: true,
    },
  },
  computed: {
    formattedCreatedDate() {
      if (!this.bill?.createdAt) return '';
      const date = this.bill.createdAt.split(' ')[1];
      if (!date) return '';
      const [day, month, year] = date.split('/');
      return `${day} tháng ${month} năm ${year}`;
    },
  },
  methods: {
    groupBillItems(listBillItems) {
      const grouped = groupBy(listBillItems, (item) => `${item.id}_${item.price}`);
      return Object.keys(grouped).map((key) => {
        const items = grouped[key];
        const info = items[0];
        const imeiCodes = items.map((i) => i.imeiCode).filter(Boolean).join(', ');
        return {
          ...info,
          imeiCodes,
          quantity: items.reduce((acc, { quantity }) => acc + quantity, 0),
        };
      });
    },
  },
};
</script>

<style scoped>
.label-color {
  color: black;
  font-weight: 600;
}
.font-size-print {
  font-size: 11px;
  color: black;
}
.print-bb-title {
  font-size: 20px;
  font-weight: bold;
  color: black;
}
.table-bordered-print th,
.table-bordered-print td {
  border: 0.5px solid #474545;
  padding: 3px 5px;
}
.imei-cell p {
  overflow-wrap: break-word;
  white-space: normal;
  line-height: 1.4;
}
</style>
